import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router';
import ContactSection from '../components/ContactSection';
import { useDarkMode } from '../context/DarkModeContext';
import { useNavigate } from 'react-router';
import { useAuth } from '../context/AuthContext';
import { createCheckoutSession, getTariffs, type TariffApiEntry } from '../lib/billingApi';
import { Shield, CreditCard, RotateCcw, CheckCircle, Clock, AlertTriangle, FolderOpen, Zap, PenLine, Link as LinkIcon, BadgeCheck, Bot, FileText, Download } from 'lucide-react';
import TestimonialSection from '../components/TestimonialSection';
import RoiCalculator from '../components/RoiCalculator';

const META_TITLE = 'BAFA-Bericht-Software – Preise & Funktionen | StartKlar.Digital';
const META_DESCRIPTION =
  'Berater Digital begleitet Sie vom Antrag bis zum Verwendungsnachweis. Tarife ab 79 €/Monat bei jährlicher Abrechnung. 30 Tage kostenlos testen.';

// ─── Preislogik ──────────────────────────────────────────────────────────────
// Preise/Limits kommen zur Laufzeit von GET /api/billing/tariffs (Backend =
// Single Source of Truth). Hier stehen nur Marketing-Texte je Tarif.

type Billing = 'monthly' | 'annual';
type TariffId = 'starter' | 'professional' | 'business' | 'flat';

/** Einmaliger Einzelbericht (Pay-per-Report) in € — eigenes Produkt, unverändert. */
export const EINZELBERICHT_PRICE = 99;

type TariffMeta = {
  id: TariffId;
  name: string;
  subtitle: string;
  badge?: string;
  highlighted: boolean;
  idealFor: string;
  features: string[];
};

export const TARIFF_META: Record<TariffId, TariffMeta> = {
  starter: {
    id: 'starter',
    name: 'Starter',
    subtitle: 'Zum Einstieg',
    highlighted: false,
    idealFor: 'Ideal, wenn Sie gelegentlich einen BAFA-Fall komplett begleiten möchten — vom Antrag bis zum Verwendungsnachweis.',
    features: [
      'Vorgänge & BAFA-Prozess verwalten',
      'Kundenverwaltung',
      'KI-gestützter Berichtseditor',
      'Fristen- & Status-Überwachung',
      'Dokumentenverwaltung',
      'Antrag & Verwendungsnachweis im Tool',
      'Unterschriften per Link anfordern',
      'E-Mail-Monitoring',
      'PDF-, DOCX- und TXT-Export',
    ],
  },
  professional: {
    id: 'professional',
    name: 'Professional',
    subtitle: 'Für aktive Berater',
    badge: 'Beliebteste Wahl',
    highlighted: true,
    idealFor: 'Ideal, wenn Sie regelmäßig BAFA-Fälle begleiten und Ihren gesamten Prozess spürbar vereinfachen möchten.',
    features: [
      'Alles aus Starter',
      'Mehr Berichte pro Monat',
      'Qualitätsprüfung & Berichtsanalyse',
      'Aufgabenverwaltung',
      'Priorisierter Support',
    ],
  },
  business: {
    id: 'business',
    name: 'Business',
    subtitle: 'Für wachsende Beratungspraxen',
    highlighted: false,
    idealFor: 'Ideal, wenn Sie mehrere BAFA-Fälle parallel begleiten und planbar skalieren möchten.',
    features: [
      'Alles aus Professional',
      'Noch mehr Berichte pro Monat',
      'Größtes jährliches Berichtsguthaben',
    ],
  },
  flat: {
    id: 'flat',
    name: 'Flat',
    subtitle: 'Für Beratungshäuser',
    highlighted: false,
    idealFor: 'Ideal, wenn mehrere Personen mit BAFA-Fällen arbeiten oder Sie den Prozess im Team standardisieren möchten.',
    features: [
      'Alles aus Business',
      'Unbegrenzt Berichte',
      'Optional: Teamoption dazubuchbar',
    ],
  },
};

const TARIFF_ORDER: TariffId[] = ['starter', 'professional', 'business', 'flat'];

function reportsLabel(t: TariffApiEntry): string {
  return t.unlimitedReports ? 'Unbegrenzt' : `${t.monthlyReportLimit} / Monat`;
}

/** Ersparnis in % bei Jahreszahlung, gerundet — bewusst "ca." in der Anzeige, da Rundungen je Tarif leicht abweichen. */
function savingsPercent(t: TariffApiEntry): number {
  return Math.round((1 - t.annualMonthlyPrice / t.monthlyPrice) * 100);
}

// ─── Vorteile ─────────────────────────────────────────────────────────────

type Benefit = { Icon: React.ElementType; headline: string; text: string };

export const BENEFITS: Benefit[] = [
  {
    Icon: Zap,
    headline: 'Bericht schneller vorbereiten',
    text: 'KI-Vorschläge für alle BAFA-Abschnitte — Sie übernehmen, passen an oder schreiben selbst. Sie starten nicht mehr bei null.',
  },
  {
    Icon: LinkIcon,
    headline: 'Kundendaten per Link einholen',
    text: 'Sie senden dem Kunden einen Link — er ergänzt fehlende Angaben direkt im Formular, ohne dass Sie alles selbst abtippen.',
  },
  {
    Icon: FileText,
    headline: 'BAFA-Antrag leichter ausfüllen',
    text: 'Antrag und Kundendaten liegen nebeneinander — Angaben können schnell übernommen werden, statt sie aus E-Mails zu suchen.',
  },
  {
    Icon: CheckCircle,
    headline: 'ESF-Fragebogen automatisch erzeugen',
    text: 'Der EU-Nachhaltigkeitsnachweis wird per Checkboxen vorbereitet und als fertiges PDF ausgegeben.',
  },
  {
    Icon: PenLine,
    headline: 'Unterschriften sauber organisieren',
    text: 'Dokumente per Knopfdruck ausfüllen, Unterschriften-Anfrage erstellen und direkt an den Kunden versenden — keine losen E-Mail-Anhänge.',
  },
  {
    Icon: Download,
    headline: 'Berichte professionell exportieren',
    text: 'PDF- und DOCX-Export mit eigenem Briefkopf, sauberen Dateinamen und automatisch angehängtem ESF-Fragebogen.',
  },
];

// ─── FAQ ───────────────────────────────────────────────────────────────────────

export const FAQ_ITEMS: { q: string; a: string }[] = [
  {
    q: 'Was zählt als Bericht?',
    a: 'Ein Bericht entspricht einem BAFA-Beratungsbericht für einen Kunden. Er zählt, sobald er angelegt wird — unabhängig davon, ob er exportiert oder eingereicht wird.',
  },
  {
    q: 'Was passiert nach meinem kostenlosen Bericht?',
    a: 'Ihr erstellter Bericht bleibt vollständig nutzbar. Für einen weiteren Bericht benötigen Sie einen kostenpflichtigen Tarif oder einen Einzelbericht.',
  },
  {
    q: 'Was kostet ein Einzelbericht?',
    a: 'Ein Einzelbericht kostet 99 € einmalig — ohne Abo. Sinnvoll, wenn Sie nur gelegentlich einen BAFA-Bericht erstellen.',
  },
  {
    q: 'Welcher Tarif passt zu mir?',
    a: 'Starter eignet sich für gelegentliche Fälle (1 Bericht/Monat), Professional für regelmäßige Arbeit (3/Monat), Business für mehrere parallele Fälle (5/Monat) und Flat für Beratungshäuser mit unbegrenztem Bedarf.',
  },
  {
    q: 'Wie funktioniert die 30-Tage-Testphase?',
    a: 'Bei jährlicher Zahlung testen Sie 30 Tage kostenlos — die erste Abbuchung erfolgt erst danach. Beim Monatsabo gibt es keine Testphase.',
  },
  {
    q: 'Was passiert mit nicht genutzten Berichten?',
    a: 'Beim Jahresabo sammelt sich Ihr monatliches Kontingent bis zu 3 Monate lang an. Beim Monatsabo verfällt ein ungenutzter Bericht zum Monatsende.',
  },
  {
    q: 'Was ist die Teamoption?',
    a: 'Für 50 € / Monat zusätzlich können Sie bis zu 5 Personen gemeinsam mit einem Account arbeiten lassen — unabhängig vom gewählten Tarif.',
  },
  {
    q: 'Was passiert nach Ablauf meines Tarifs?',
    a: 'Der Tarif verlängert sich automatisch — monatlich oder jährlich, je nach gewählter Abrechnung. Sie können jederzeit zum Ende des Abrechnungszeitraums kündigen. Ihre Berichte und Daten bleiben in jedem Fall erhalten.',
  },
  {
    q: 'Was ist im Startklar-Programm enthalten?',
    a: 'Das Startklar-Programm begleitet Sie 6 Monate persönlich auf dem Weg zum selbständigen BAFA-Berater — inklusive Zugang zum Tool und gemeinsamer Arbeit an bis zu 3 BAFA-Berichten pro Monat. 590 € / Monat.',
  },
  {
    q: 'Ist Berater Digital ein offizielles BAFA-Portal?',
    a: 'Nein. Berater Digital ist kein offizielles Portal der BAFA. Es ist ein Arbeitstool für BAFA-Berater, das Sie von der Antragstellung bis zum Verwendungsnachweis unterstützt.',
  },
  {
    q: 'Erstellt die KI den Bericht komplett automatisch?',
    a: 'Nein. Die KI liefert strukturierte Vorschläge für die Berichtsabschnitte. Sie prüfen, ändern und finalisieren die Inhalte selbst. Die fachliche Verantwortung bleibt bei Ihnen.',
  },
  {
    q: 'Was passiert mit Kundendaten?',
    a: 'Kundendaten werden ausschließlich auf Servern in Deutschland verarbeitet und gespeichert. Sie werden nicht an Dritte weitergegeben oder für KI-Training verwendet.',
  },
];

const FAQ_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ_ITEMS.map((item) => ({
    '@type': 'Question',
    name: item.q,
    acceptedAnswer: { '@type': 'Answer', text: item.a },
  })),
};

// ─── Komponente ────────────────────────────────────────────────────────────────

export default function Preise() {
  const { darkMode } = useDarkMode();
  // Jahresabo ist Default — klar finanziell & funktional im Vorteil (20% günstiger, Trial, Rollover).
  const [billing, setBilling] = useState<Billing>('annual');
  const [teamOption, setTeamOption] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const location = useLocation();
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState('');

  const [tariffs, setTariffs] = useState<TariffApiEntry[]>([]);
  const [teamOptionInfo, setTeamOptionInfo] = useState({ priceMonthly: 50, totalSeats: 5 });
  const [trialDaysAnnual, setTrialDaysAnnual] = useState(30);
  const [tariffsLoading, setTariffsLoading] = useState(true);

  useEffect(() => {
    getTariffs()
      .then((res) => {
        setTariffs(res.tariffs);
        setTeamOptionInfo(res.teamOption);
        setTrialDaysAnnual(res.trialDaysAnnual);
      })
      .catch(() => setCheckoutError('Preise konnten nicht geladen werden.'))
      .finally(() => setTariffsLoading(false));
  }, []);

  const tariffByCode = (code: TariffId) => tariffs.find((t) => t.code === code);

  // Tarif gilt fürs ganze Team — nur Owner/Admin dürfen ihn ändern (Mitglieder
  // nutzen ihn automatisch mit, ohne selbst zu bezahlen).
  const canManageBilling = !user?.teamId || user?.teamRole === 'owner' || user?.teamRole === 'admin';

  const handleCheckout = async (plan: TariffId | 'einzelbericht') => {
    if (!user) {
      navigate('/register?redirect=/preise');
      return;
    }
    if (!canManageBilling) {
      setCheckoutError('Nur der Team-Inhaber oder Team-Admin kann den Tarif ändern.');
      return;
    }
    setCheckoutLoading(plan);
    setCheckoutError('');
    try {
      await createCheckoutSession(plan, billing, plan !== 'einzelbericht' ? teamOption : undefined);
    } catch (err: any) {
      setCheckoutError(err.message || 'Fehler beim Öffnen des Checkouts');
    } finally {
      setCheckoutLoading(null);
    }
  };

  useEffect(() => {
    const hash = location.hash.slice(1);
    if (hash) {
      setTimeout(() => {
        document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' });
        navigate(location.pathname, { replace: true });
      }, 300);
    }
  }, [location.hash]);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = META_TITLE;
    let descTag = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const hadDescTag = !!descTag;
    const previousDesc = descTag?.content ?? '';
    if (!descTag) {
      descTag = document.createElement('meta');
      descTag.name = 'description';
      document.head.appendChild(descTag);
    }
    descTag.content = META_DESCRIPTION;
    return () => {
      document.title = previousTitle;
      if (hadDescTag && descTag) descTag.content = previousDesc;
      else if (descTag) descTag.remove();
    };
  }, []);

  // Liquid-Glass-Look (Pilot). Das Material trägt sich selbst — Hintergrund, Kante und
  // Schatten kommen aus lg-*; die Abschnitte darunter bleiben transparent, damit der
  // Lichtstrich der Bühne durchscheint. Funktioniert hell wie dunkel.
  // docs/superpowers/specs/2026-07-11-liquid-glass-preisseite-design.md
  const bg = darkMode ? 'lg-stage text-slate-200' : 'lg-stage text-slate-700';
  const heading = darkMode ? 'text-white' : 'text-slate-900';
  const muted = darkMode ? 'text-slate-300/85' : 'text-slate-600';
  const card = 'lg-surface';
  const ctaPrimary = 'lg-btn px-6 py-3 font-semibold';
  const ctaSecondary = 'lg-btn lg-btn-quiet px-6 py-3 font-semibold';
  // Abschnitte tragen keinen eigenen Grund mehr (sonst verdeckt er den Lichtstrich).
  const sectionBg = '';

  return (
    <div className={`min-h-screen ${bg}`}>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(FAQ_JSON_LD) }} />

      {/* ── 1. HERO ─────────────────────────────────────────────────── */}
      <section className={`py-16 md:py-24 px-4 ${sectionBg}`}>
        <div className="max-w-3xl mx-auto text-center">

          {/* Text */}
          <div>
            <span className={`lg-pill inline-block text-xs font-semibold px-3 py-1 mb-5 ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>
              Aus der BAFA-Beratungspraxis entwickelt
            </span>

            {/* Doppelte Größe: 48px → 96px (6rem) ab Desktop. Auf dem Handy ist die
                Verdopplung nicht möglich — "BAFA-Berichte" braucht bei 72px 518px
                Breite, der Container hat bei 390px aber nur 358px, das Wort risse am
                Bindestrich. Daher fließend statt fester Stufen: 11.5vw wächst mit dem
                Bildschirm und bleibt überall unter der Bruchgrenze (bei 375px liegt
                die bei 47px), gedeckelt auf 96px. */}
            <h1
              className={`text-[clamp(2.25rem,11.5vw,6rem)] font-bold leading-tight mb-4 ${heading}`}
            >
              BAFA-Berichte ohne{' '}
              {/* Schreibschriften wirken bei gleicher Punktgröße kleiner, weil die
                  Kleinbuchstaben flacher sind — 1.1em und der leichte Versatz nach
                  unten setzen "Papierkram" optisch auf dieselbe Zeile wie den Rest. */}
              <span className="font-script text-[1.1em] leading-none inline-block translate-y-[0.02em]">
                Papierkram
              </span>
            </h1>

            <p className={`text-lg md:text-xl mb-8 leading-relaxed ${muted}`}>
              Berater Digital unterstützt Sie vom Kundentermin bis zum fertigen Bericht — mit KI-gestütztem Editor, Kundendaten per Link, Unterschriften, ESF-Fragebogen und professionellem Export. Sie behalten dabei jederzeit die fachliche Kontrolle.
            </p>

            {/* TODO: Produktscreenshot hier einfügen — Browser auf 1280px, Report-Editor mit echtem Bericht */}

            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/register" className={ctaPrimary}>Ersten Bericht kostenlos erstellen</Link>
            </div>
          </div>


        </div>
      </section>

      {/* ── TRUST-LEISTE ────────────────────────────────────────── */}
      <section className={`py-8 px-4 border-y ${darkMode ? 'border-white/10' : 'border-slate-900/5'} ${sectionBg}`}>
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            {[
              { Icon: Shield,      label: 'Daten in Deutschland' },
              { Icon: CreditCard,  label: 'Sichere Zahlung' },
              { Icon: RotateCcw,   label: 'Jederzeit kündbar' },
              { Icon: CheckCircle, label: '30 Tage kostenlos testen (Jahresabo)' },
              { Icon: Bot,         label: 'KI-gestützt' },
            ].map(({ Icon, label }) => (
              <div key={label} className="flex flex-col items-center text-center gap-2">
                <Icon size={48} className={darkMode ? 'text-blue-400' : 'text-blue-600'} />
                <span className={`text-base font-medium ${muted}`}>{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 2. SCHMERZPUNKTE ────────────────────────────────────────── */}
      <section className={`py-16 px-4 ${sectionBg}`}>
        <div className="max-w-6xl mx-auto">
          <h2 className={`text-2xl md:text-3xl font-bold text-center mb-10 ${heading}`}>
            Warum BAFA-Berichte so viel Nerven kosten?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {([
              { Icon: Clock,         title: 'Zu viel Routinearbeit',      text: 'Berichtsteile folgen immer derselben Struktur — trotzdem wird jedes Mal neu formuliert.' },
              { Icon: AlertTriangle, title: 'Verstreute Kundendaten', text: 'Stammdaten, Dokumente, Rückfragen — verteilt auf E-Mails, PDFs und Chatverläufe.' },
              { Icon: FolderOpen,    title: 'Unterschriften und Anlagen',  text: 'Berichte, Vollmachten, Uploads laufen über mehrere Nachrichten hin und her.' },
            ] as const).map(({ Icon, title, text }) => (
              <div key={title} className="lg-surface lg-card p-6 text-center">
                <h3 className={`font-semibold text-lg mb-2 ${darkMode ? 'text-blue-200' : 'text-blue-800'}`}>{title}</h3>
                <p className={`text-sm leading-relaxed ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 3. VORTEILE ──────────────────────────────────────────────── */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${heading}`}>
            Ihr BAFA-Prozess neu gedacht
          </h2>
          <p className={`text-lg max-w-2xl mx-auto ${muted}`}>
            Vom Kundentermin bis zum fertigen Bericht — Schritt für Schritt unterstützt.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {BENEFITS.map(({ Icon, headline, text }) => (
            <div key={headline} className={`rounded-2xl border p-6 flex gap-4 ${card}`}>
              <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${darkMode ? 'bg-blue-500/20' : 'bg-blue-50'}`}>
                <Icon size={20} className={darkMode ? 'text-blue-400' : 'text-blue-600'} />
              </div>
              <div>
                <h3 className={`font-semibold text-lg mb-2 ${heading}`}>{headline}</h3>
                <p className={`text-sm leading-relaxed ${muted}`}>{text}</p>
              </div>
            </div>
          ))}
        </div>
        <p className={`text-center mt-10 text-base max-w-2xl mx-auto ${muted}`}>
          Berater Digital strukturiert Ihren gesamten BAFA-Prozess — von der ersten Kundenanfrage bis zum fertigen Bericht.
        </p>
        <div className="text-center mt-6">
          <Link to="/register" className={ctaPrimary}>Ersten Bericht kostenlos erstellen</Link>
        </div>
        </div>
      </section>

      {/* ── 4. ROI-RECHNER ──────────────────────────────────────────── */}
      <section
        className={`py-16 px-4 ${sectionBg}`}
        aria-labelledby="roi-heading"
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <span className={`lg-pill inline-block text-xs font-semibold px-3 py-1 mb-3 ${darkMode ? 'text-emerald-300' : 'text-emerald-700'}`}>
              Was spare ich wirklich?
            </span>
            <h2 id="roi-heading" className={`text-2xl md:text-3xl font-bold ${heading}`}>
              Rechnen Sie sich Ihre Ersparnis aus
            </h2>
            <p className={`mt-2 text-sm ${muted}`}>
              Tragen Sie Ihre Werte ein — Defaults basieren auf Erfahrungswerten für BAFA-Berichte.
            </p>
          </div>
          <RoiCalculator darkMode={darkMode} variant="glass" />
        </div>
        <div className="text-center mt-10 flex flex-wrap justify-center gap-4">
          <Link to="/register" className={ctaPrimary}>Jetzt starten</Link>
          <a href="#kontaktformular" className={ctaSecondary}>Kontakt aufnehmen</a>
        </div>
      </section>

      {/* ── TESTIMONIALS (ausgeblendet bis echte Daten vorhanden) ── */}
      <TestimonialSection testimonials={[]} />

      {/* ── 5. PREISE ───────────────────────────────────────────────── */}
      <section className={`py-20 px-4 ${sectionBg}`} id="preise">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${heading}`}>Einfache, transparente Preise</h2>
            <p className={`mb-6 ${muted}`}>Starten Sie kostenlos mit Ihrem ersten Bericht. Danach wählen Sie den Tarif, der zu Ihrer Arbeitsweise passt.</p>

            {/* Billing-Toggle — Glas-Segmentregler */}
            <div className="lg-pill inline-flex items-center gap-1 p-1">
              <button
                onClick={() => setBilling('annual')}
                className={`px-4 py-2 rounded-full text-sm font-semibold cursor-pointer transition active:scale-95 ${
                  billing === 'annual'
                    ? darkMode ? 'bg-white/25 text-white shadow' : 'bg-white/90 text-slate-900 shadow'
                    : muted
                }`}
              >
                Jährlich <span className="text-emerald-500 font-bold">ca. −20 %</span>
              </button>
              <button
                onClick={() => setBilling('monthly')}
                className={`px-4 py-2 rounded-full text-sm font-semibold cursor-pointer transition active:scale-95 ${
                  billing === 'monthly'
                    ? darkMode ? 'bg-white/25 text-white shadow' : 'bg-white/90 text-slate-900 shadow'
                    : muted
                }`}
              >
                Monatlich
              </button>
            </div>

            {/* Vorteile/Nachteile je Intervall — Jahresabo soll klar attraktiver wirken */}
            <div className={`mt-5 max-w-xl mx-auto text-sm ${muted}`}>
              {billing === 'annual' ? (
                <ul className="flex flex-wrap justify-center gap-x-5 gap-y-1.5">
                  <li>✓ ca. 20 % günstiger</li>
                  <li>✓ {trialDaysAnnual} Tage kostenlos testen</li>
                  <li>✓ ungenutzte Berichte bis zu 3 Monate mitnehmen</li>
                  <li>✓ persönliches Onboarding</li>
                  <li>✓ bevorzugter Support</li>
                </ul>
              ) : (
                <ul className="flex flex-wrap justify-center gap-x-5 gap-y-1.5">
                  <li>flexibel monatlich</li>
                  <li>keine Testphase</li>
                  <li>nicht genutzte Berichte verfallen monatlich</li>
                </ul>
              )}
            </div>
          </div>

          {checkoutError && (
            <div className="mb-4 p-3 rounded-lg bg-red-100 text-red-700 text-sm text-center">
              {checkoutError}
            </div>
          )}

          {/* Teamoption — gilt für alle Tarife gleich, einmal zentral wählbar */}
          <div className="flex justify-center mb-8">
            <label className={`lg-pill inline-flex items-center gap-2 px-4 py-2 text-sm font-medium cursor-pointer ${muted}`}>
              <input
                type="checkbox"
                checked={teamOption}
                onChange={(e) => setTeamOption(e.target.checked)}
                className="w-4 h-4"
              />
              Teamoption dazubuchen — +{teamOptionInfo.priceMonthly} € / Monat, bis zu {teamOptionInfo.totalSeats} Mitglieder
            </label>
          </div>

          {/* 4 Preiskarten */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {TARIFF_ORDER.map((id) => {
              const meta = TARIFF_META[id];
              const data = tariffByCode(id);
              const price = data ? (billing === 'annual' ? data.annualMonthlyPrice : data.monthlyPrice) : null;

              return (
                <div
                  key={id}
                  className={`lg-surface lg-card relative flex flex-col p-6 lg:p-8 transition-all duration-200 ${
                    meta.highlighted ? 'lg-featured md:-translate-y-2' : 'hover:-translate-y-1'
                  }`}
                >
                  {meta.badge && (
                    <span className="lg-badge absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 text-xs font-semibold whitespace-nowrap z-10">
                      {meta.badge}
                    </span>
                  )}
                  <h3 className={`text-xl font-semibold mb-1 ${heading}`}>{meta.name}</h3>
                  <p className={`text-sm mb-6 ${muted}`}>{meta.subtitle}</p>

                  <div className="mb-6">
                    <div className="flex items-baseline gap-1">
                      <span className={`text-4xl font-bold ${heading}`}>{price != null ? `${price} €` : '—'}</span>
                      <span className={`text-sm ${muted}`}>/ Monat</span>
                    </div>
                    {billing === 'annual' && data && (
                      <p className="text-xs mt-1 text-emerald-500 font-medium">
                        bei jährlicher Abrechnung · ca. {savingsPercent(data)} % günstiger
                      </p>
                    )}
                    {billing === 'monthly' && (
                      <p className={`text-xs mt-1 ${muted}`}>monatliche Abrechnung</p>
                    )}
                    <p className={`text-xs mt-2 font-medium ${heading}`}>
                      {data ? reportsLabel(data) : '…'} Berichte
                    </p>
                  </div>

                  <ul className="mb-6 space-y-2">
                    {meta.features.map((f) => (
                      <li key={f} className={`flex items-start gap-2 text-sm ${muted}`}>
                        <span className={`mt-0.5 flex-shrink-0 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>✓</span>
                        {f}
                      </li>
                    ))}
                  </ul>

                  <div className="mt-auto">
                    <button
                      type="button"
                      onClick={() => handleCheckout(id)}
                      disabled={!!checkoutLoading || tariffsLoading}
                      className={`lg-btn w-full px-5 py-3 font-semibold disabled:opacity-60 ${
                        meta.highlighted ? '' : 'lg-btn-quiet'
                      }`}
                    >
                      {checkoutLoading === id ? 'Wird geladen…' : `${meta.name} starten`}
                    </button>
                    <p className={`mt-3 text-xs text-center leading-relaxed ${muted}`}>{meta.idealFor}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <p className={`text-center text-xs max-w-2xl mx-auto -mt-6 mb-12 ${muted}`}>
            Ihren ersten Bericht erstellen Sie kostenlos, ohne Kreditkarte — <Link to="/register" className="underline">jetzt starten</Link>.
          </p>

          {/* Einzelbericht + Startklar-Programm */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className={`lg-card p-6 md:p-8 flex flex-col ${card}`}>
              <span className={`lg-pill inline-block self-start text-xs font-semibold px-3 py-1 mb-3 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                Einzelbericht
              </span>
              <h3 className={`text-xl font-bold mb-3 ${heading}`}>Nur gelegentlich einen Bericht?</h3>
              <p className={`mb-4 text-sm ${muted}`}>
                Kaufen Sie einen einzelnen Bericht für {EINZELBERICHT_PRICE} € — ohne Abo. Ideal für Berater, die nur ein- oder zweimal im Jahr einen BAFA-Bericht erstellen.
              </p>
              <ul className={`mb-6 space-y-1 text-sm ${muted}`}>
                <li className="flex items-start gap-2"><span className={`flex-shrink-0 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>✓</span>Kein monatliches Abo</li>
                <li className="flex items-start gap-2"><span className={`flex-shrink-0 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>✓</span>Einmalig zahlen, sofort nutzen</li>
                <li className="flex items-start gap-2"><span className={`flex-shrink-0 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>✓</span>Alle Funktionen enthalten</li>
              </ul>
              <div className="mt-auto">
                <button
                  type="button"
                  onClick={() => handleCheckout('einzelbericht')}
                  disabled={!!checkoutLoading}
                  className="lg-btn lg-btn-quiet w-full px-5 py-3 font-semibold disabled:opacity-60"
                >
                  {checkoutLoading === 'einzelbericht' ? 'Wird geladen…' : `Einzelbericht kaufen — ${EINZELBERICHT_PRICE} €`}
                </button>
              </div>
            </div>

            <div className="lg-surface lg-card p-6 md:p-8 flex flex-col">
              <span className={`lg-pill inline-block self-start text-xs font-semibold px-3 py-1 mb-3 ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                Coaching-Programm
              </span>
              <h3 className={`text-xl font-bold mb-1 ${heading}`}>Persönliche Begleitung gewünscht?</h3>
              <p className={`text-sm font-semibold mb-3 ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>Startklar-Programm</p>
              <p className={`mb-4 text-sm ${muted}`}>
                6 Monate Unterstützung, bis zu 3 BAFA-Berichte pro Monat, Zugang zum Tool inklusive. Geeignet, wenn Sie:
              </p>
              <ul className={`mb-4 space-y-1 text-sm ${muted}`}>
                <li className="flex items-start gap-2"><span className={`flex-shrink-0 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>✓</span>Erste BAFA-Berichte sicherer angehen möchten</li>
                <li className="flex items-start gap-2"><span className={`flex-shrink-0 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>✓</span>Ihren Prozess professionell aufsetzen wollen</li>
                <li className="flex items-start gap-2"><span className={`flex-shrink-0 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>✓</span>BAFA-Beratung als wiederholbaren Prozess entwickeln möchten</li>
              </ul>
              <p className={`mb-6 text-xs ${muted}`}>
                Das Programm wird nach persönlicher Kontaktaufnahme besprochen.
              </p>
              <div className="mt-auto">
                <a
                  href="#kontaktformular"
                  className="lg-btn w-full px-5 py-3 font-semibold"
                >
                  Startklar-Programm anfragen
                </a>
              </div>
            </div>
          </div>

          {/* Garantien */}
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              { Icon: BadgeCheck,    text: '30 Tage kostenlos testen',     sub: 'Beim Jahresabo, erste Abbuchung erst danach.' },
              { Icon: RotateCcw,     text: 'Jederzeit kündbar',            sub: 'Zum Ende des Abrechnungszeitraums.' },
              { Icon: Shield,        text: 'Ihre Daten gehören Ihnen',     sub: 'Server in Deutschland.' },
              { Icon: AlertTriangle, text: 'Kein offizielles BAFA-Portal', sub: 'Ein Arbeitstool für BAFA-Berater.' },
              { Icon: Bot,           text: 'KI erstellt Vorschläge',       sub: 'Sie prüfen und finalisieren selbst.' },
            ].map(({ Icon, text, sub }) => (
              <div key={text} className={`flex items-start gap-4 p-5 rounded-2xl border ${darkMode ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-emerald-50 border-emerald-100'}`}>
                <div className={`flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center ${darkMode ? 'bg-emerald-500/20' : 'bg-emerald-100'}`}>
                  <Icon size={22} className={darkMode ? 'text-emerald-400' : 'text-emerald-600'} />
                </div>
                <div>
                  <p className={`font-semibold text-sm ${darkMode ? 'text-emerald-200' : 'text-emerald-800'}`}>{text}</p>
                  <p className={`text-xs mt-0.5 ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>{sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 7. ÜBER DEN ENTWICKLER ──────────────────────────────────── */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row items-center gap-12">
          <div className="flex-1 order-2 md:order-1">
            <h2 className={`text-2xl md:text-3xl font-bold mb-5 ${heading}`}>
              Berater Digital — aus der Praxis entwickelt
            </h2>
            <p className={`text-base leading-relaxed mb-4 ${muted}`}>
              Wir haben Berater Digital entwickelt, weil wir selbst zu viel Zeit mit Berichten, Kundendaten und Unterschriften verbracht haben. Zu wenig Zeit für das, wofür wir eigentlich bezahlt werden: die Beratung.
            </p>
            <p className={`text-base leading-relaxed mb-6 ${muted}`}>
              Berater Digital löst die Probleme, die uns täglich begegnen.
            </p>
            <p className={`text-base mb-4 font-medium ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
              Kennen Sie das auch? Lassen Sie sich kostenlos beraten.
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href="tel:+4915207429338"
                className="lg-btn lg-btn-quiet gap-2 px-5 py-2.5 font-semibold"
              >
                Anrufen
              </a>
              <a
                href="https://wa.me/4915207429338"
                target="_blank"
                rel="noopener noreferrer"
                className="lg-btn lg-tint-green gap-2 px-5 py-2.5 font-semibold"
              >
                WhatsApp
              </a>
            </div>
          </div>
          <div className="flex flex-shrink-0 gap-4 order-1 md:order-2">
            {[
              { src: '/andreas-helm.png', name: 'Andreas Helm', role: 'BAFA-Berater & Entwickler', zoom: false },
              { src: '/alex-jackih.png',  name: 'Alex Jackih',  role: 'Entwicklung & Umsetzung',  zoom: true  },
            ].map(({ src, name, role, zoom }) => (
              <div key={name} className="flex flex-col">
                <div className={`w-32 h-36 md:w-64 md:h-72 rounded-t-2xl overflow-hidden shadow-lg border-x border-t ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                  <img
                    src={src}
                    alt={name}
                    className={`w-full h-full object-cover ${zoom ? 'scale-125 translate-y-7' : ''}`}
                  />
                </div>
                <div className={`rounded-b-2xl border-x border-b px-3 py-2 ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                  <p className={`font-semibold text-sm ${heading}`}>{name}</p>
                  <p className={`text-xs ${muted}`}>{role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        </div>
      </section>

      {/* ── 8. FAQ ──────────────────────────────────────────────────── */}
      <section className={`py-16 px-4 ${sectionBg}`}>
        <div className="max-w-3xl mx-auto">
          <h2 className={`text-2xl md:text-3xl font-bold text-center mb-10 ${heading}`}>
            Häufige Fragen
          </h2>
          <div className="space-y-4">
            {FAQ_ITEMS.map((item) => (
              <details
                key={item.q}
                className="lg-surface group p-4"
              >
                <summary className={`cursor-pointer list-none flex justify-between items-center ${heading}`}>
                  <h3 className="font-semibold text-base">{item.q}</h3>
                  <span className="ml-4 transition-transform group-open:rotate-45 text-2xl leading-none flex-shrink-0">+</span>
                </summary>
                <p className={`mt-3 text-sm leading-relaxed ${muted}`}>{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>


      {/* ── 10. FINALER CTA ──────────────────────────────────────────── */}
      <section className="max-w-3xl mx-auto px-4 py-20 text-center">
        <h2 className={`text-2xl md:text-3xl font-bold mb-3 ${heading}`}>
          Bereit Ihren BAFA-Prozess zu vereinfachen?
        </h2>
        <p className={`mb-8 max-w-xl mx-auto ${muted}`}>
          Erstellen Sie Ihren ersten Bericht kostenlos und prüfen Sie selbst, ob Berater Digital zu Ihrer Arbeitsweise passt. Kein Risiko. Keine Kreditkarte für den Testbericht. Sie behalten die fachliche Kontrolle.
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <Link to="/register" className={ctaPrimary}>Ersten Bericht kostenlos erstellen</Link>
          <a href="#kontaktformular" className={ctaSecondary}>
            Kontakt aufnehmen
          </a>
        </div>
      </section>

      {/* ── 11. KONTAKTFORMULAR ──────────────────────────────────────── */}
      <section id="kontaktformular" className={`py-16 px-4 ${sectionBg}`}>
        <div className="max-w-5xl mx-auto">
          <h2 className={`text-2xl font-bold text-center mb-2 ${heading}`}>Direkt Kontakt aufnehmen</h2>
          <p className={`text-center mb-8 ${muted}`}>Wir melden uns in der Regel innerhalb eines Werktages.</p>
          <ContactSection initialSubject="Vertrieb / Preise" initialMessage="Ich bin unsicher, welcher Plan zu mir passt, und wünsche eine unverbindliche Beratung." />
        </div>
      </section>
    </div>
  );
}
